import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@/components/forge/Icon'
import { TierGlyph } from '@/components/forge/TierGlyph'
import { Mono, Num } from '@/components/forge/primitives'
import { tagLabel } from '@/features/sensorCharts/tree'
import { cn } from '@/lib/cn'

/**
 * Place → sensor → tag, as an expandable tree.
 *
 * LIVE. One component in two modes, so the browser and the review pane are the
 * same picture with the unchosen taken away rather than two layouts:
 *
 *  - `select` — every tag has a checkbox. Already-on-the-chart tags (`locked`)
 *    show ticked and disabled. With `atCapacity`, unticked tags disable too:
 *    the server refuses a 21st tag, so the control does not offer one.
 *  - `review` — the tree as handed in (already pruned to the chosen tags by
 *    `pickTags`), fully open, with a Remove control per tag.
 *
 * ── Expansion is local, selection is not ──────────────────────────────────
 * What is open is this component's own state; what is chosen belongs to the
 * caller. So collapsing a branch never drops a tick, and the review pane and the
 * browser stay in step because they read one set.
 *
 * Built on native buttons with `aria-expanded` and real checkboxes rather than
 * an ARIA tree widget: the roving-tabindex keyboard model of `role="tree"` is
 * a lot of contract to get exactly right, and a list of disclosure buttons is
 * fully operable as it stands.
 *
 * @param {object} props
 * @param {object} props.tree            From `buildSensorTree` / `pickTags`.
 * @param {'select'|'review'} [props.mode]
 * @param {Set<string>} [props.selected]
 * @param {Set<string>} [props.locked]
 * @param {(tagId: string) => void} [props.onToggle]
 * @param {(tagId: string) => void} [props.onRemove]
 * @param {(unitId: string) => string} [props.unitSymbol]
 * @param {boolean} [props.atCapacity]
 * @param {boolean} [props.disabled]
 */
export function SensorTagTree({
  tree,
  mode = 'select',
  selected = EMPTY,
  locked = EMPTY,
  onToggle,
  onRemove,
  unitSymbol = () => '',
  atCapacity = false,
  disabled = false,
}) {
  const review = mode === 'review'
  /* Open by default: the root, and in review mode everything. A key set rather
     than a closed set, so a branch that appears after a refetch starts shut and
     does not push the reader's place down the list. */
  const [open, setOpen] = useState(() => new Set([tree.key]))
  const isOpen = (key) => review || open.has(key)
  const flip = (key) =>
    setOpen((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const context = { review, selected, locked, onToggle, onRemove, unitSymbol, atCapacity, disabled, isOpen, flip }

  return (
    <ul className="m-0 list-none p-0">
      <PlaceNode node={tree} depth={0} context={context} />
    </ul>
  )
}

const EMPTY = new Set()

/** Inline indent per depth. Logical, so it mirrors in RTL. */
const indent = (depth) => ({ paddingInlineStart: `${0.75 + depth * 1.25}rem` })

function PlaceNode({ node, depth, context }) {
  const { t } = useTranslation(['app', 'common'])
  const open = context.isOpen(node.key)
  const name = node.name ?? t('sensors.charts.tree.unknownPlace')

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => context.flip(node.key)}
        disabled={context.review}
        className={cn(
          'flex w-full items-center gap-2.5 border-b border-subtle py-2 pe-3 text-start',
          !context.review && 'cursor-pointer transition-colors hover:bg-sunken',
          depth === 0 && 'bg-sunken',
        )}
        style={indent(depth)}
      >
        {context.review ? null : (
          <Icon name={open ? 'down' : 'right'} size="xs" className="mirror-rtl shrink-0 text-ink-mute" />
        )}
        <TierGlyph tier={node.tier} />
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-body-sm text-ink', depth === 0 && 'font-semibold')}>
            {name}
          </span>
          <span className="eyebrow mt-px block">
            {t(`settings.hierarchy.tiers.${node.tier}`, { defaultValue: node.tier })}
          </span>
        </span>
        {node.code ? <Mono className="shrink-0 text-ink-dim">{node.code}</Mono> : null}
        <Num className="shrink-0 text-micro font-normal text-ink-dim">
          {t('sensors.charts.tree.tagCount', { tags: node.tagCount })}
        </Num>
      </button>

      {open ? (
        <ul className="m-0 list-none p-0">
          {node.sensors.length ? (
            <>
              {/* "Sensors on this line" only when there is also something
                  beneath it to tell them apart from. */}
              {node.children.length ? (
                <li
                  className="eyebrow border-b border-subtle py-1.5 pe-3"
                  style={indent(depth + 1)}
                >
                  {t(`sensors.charts.tree.ownSensors.${node.tier}`, {
                    defaultValue: t('sensors.charts.tree.ownSensors.asset'),
                  })}
                </li>
              ) : null}
              {node.sensors.map((sensor) => (
                <SensorNode
                  key={sensor.id}
                  sensor={sensor}
                  parentKey={node.key}
                  depth={depth + 1}
                  context={context}
                />
              ))}
            </>
          ) : null}
          {node.children.map((child) => (
            <PlaceNode key={child.key} node={child} depth={depth + 1} context={context} />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

function SensorNode({ sensor, parentKey, depth, context }) {
  const { t } = useTranslation(['app', 'common'])
  const key = `${parentKey}/sensor:${sensor.id}`
  const open = context.isOpen(key)
  const chosen = sensor.tags.filter((tag) => context.selected.has(tag.id)).length

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => context.flip(key)}
        disabled={context.review}
        className={cn(
          'flex w-full items-center gap-2.5 border-b border-subtle py-2 pe-3 text-start',
          !context.review && 'cursor-pointer transition-colors hover:bg-sunken',
        )}
        style={indent(depth)}
      >
        {context.review ? null : (
          <Icon name={open ? 'down' : 'right'} size="xs" className="mirror-rtl shrink-0 text-ink-mute" />
        )}
        <Icon name="gauge" size="sm" className="shrink-0 text-on-forge" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-sm text-ink">{sensor.name}</span>
          <span className="eyebrow mt-px block">{t('sensors.charts.tree.sensor')}</span>
        </span>
        {!context.review && chosen ? (
          <Num className="shrink-0 text-micro font-semibold text-on-forge">
            {t('sensors.charts.tree.chosen', { chosen })}
          </Num>
        ) : null}
        <Num className="shrink-0 text-micro font-normal text-ink-dim">
          {t('sensors.charts.tree.tagCount', { tags: sensor.tags.length })}
        </Num>
      </button>

      {open ? (
        sensor.tags.length ? (
          <ul className="m-0 list-none p-0">
            {sensor.tags.map((tag) => (
              <TagRow key={tag.id} tag={tag} depth={depth + 1} context={context} />
            ))}
          </ul>
        ) : (
          <p
            className="m-0 border-b border-subtle py-2 pe-3 text-body-sm text-ink-mute"
            style={indent(depth + 1)}
          >
            {t('sensors.charts.tree.noTags')}
          </p>
        )
      ) : null}
    </li>
  )
}

function TagRow({ tag, depth, context }) {
  const { t } = useTranslation(['app', 'common'])
  const unit = tag.valueUnitId ? context.unitSymbol(tag.valueUnitId) : ''
  const isLocked = context.locked.has(tag.id)
  const isChosen = isLocked || context.selected.has(tag.id)
  const plottable = !tag.valueType || tag.valueType === 'float' || tag.valueType === 'integer'

  const meta = (
    <span className="mt-px block truncate text-micro font-normal text-ink-dim">
      {[
        tag.name && tag.description ? tag.description : null,
        unit || null,
        tag.valueType ? t(`dataFusion.sensors.valueType.${tag.valueType}`, { defaultValue: tag.valueType }) : null,
        isLocked ? t('sensors.charts.tree.onChart') : null,
        plottable ? null : t('sensors.charts.tree.notPlottable'),
      ]
        .filter(Boolean)
        .join(' · ')}
    </span>
  )

  if (context.review) {
    return (
      <li
        className="flex items-center gap-2.5 border-b border-subtle py-2 pe-2"
        style={indent(depth)}
      >
        <Icon name="wave" size="xs" className="shrink-0 text-ink-mute" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-sm text-ink">{tagLabel(tag, t)}</span>
          {meta}
        </span>
        {context.onRemove ? (
          <button
            type="button"
            disabled={context.disabled}
            onClick={() => context.onRemove(tag.id)}
            aria-label={t('sensors.charts.tree.remove', { tag: tagLabel(tag, t) })}
            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-sm text-ink-mute transition-colors hover:bg-sunken hover:text-on-red disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon name="x" size="sm" />
          </button>
        ) : null}
      </li>
    )
  }

  const blocked = isLocked || context.disabled || (context.atCapacity && !isChosen)

  return (
    <li>
      <label
        className={cn(
          'flex items-center gap-2.5 border-b border-subtle py-2 pe-3',
          // The earned leading edge: it marks a chosen tag and nothing else.
          isChosen ? 'border-s-[3px] border-s-forge' : 'border-s-[3px] border-s-transparent',
          blocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-sunken',
        )}
        style={indent(depth)}
      >
        <input
          type="checkbox"
          className="shrink-0 accent-forge"
          checked={isChosen}
          disabled={blocked}
          onChange={() => context.onToggle?.(tag.id)}
        />
        <Icon name="wave" size="xs" className="shrink-0 text-ink-mute" />
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-body-sm text-ink', isChosen && 'font-semibold')}>
            {tagLabel(tag, t)}
          </span>
          {meta}
        </span>
      </label>
    </li>
  )
}
